import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAddResponse,
  useAuth,
  useCloseMatch,
  useGetMatch,
  useGetProfile,
  useGetResponses,
} from "../hooks";
import { shareMessage } from "../i18n/messages";
import { APP_HOME_PATH } from "../lib/app-paths";
import { formatTime } from "../lib/format";

const MAX_PLAYERS = 4;

export const MatchStatusPage = () => {
  const { t } = useTranslation();
  const { matchId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: match, isLoading, error } = useGetMatch(matchId);
  const { data: responses = [] } = useGetResponses(matchId);
  const { user, isAuthenticated } = useAuth();
  const { data: profile } = useGetProfile(user?.id);
  const { mutate: closeMatch, isPending: isClosing } = useCloseMatch();
  const { mutate: addResponse, isPending: isJoining } = useAddResponse();
  const [confirmClose, setConfirmClose] = useState(false);
  const [actionError, setActionError] = useState("");
  const [copied, setCopied] = useState(false);

  if (isLoading) {
    return <p className="pp-status">{t("common.loading")}</p>;
  }

  if (error || !match) {
    return (
      <main className="pp-screen">
        <h1>{t("common.error")}</h1>
        <p className="pp-subtitle">
          {error instanceof Error ? error.message : t("status.notFound")}
        </p>
        <Link to={APP_HOME_PATH} className="pp-btn">
          {t("common.backToHome")}
        </Link>
      </main>
    );
  }

  const missing = match.spotsNeeded;
  const isComplete = missing === 0 || match.status === "closed";
  const confirmed = Math.max(MAX_PLAYERS - missing, 0);
  const progress = (confirmed / MAX_PLAYERS) * 100;
  const isOwner = Boolean(user && match.ownerId === user.id);
  const isAlreadyRegistered = Boolean(
    user && responses.some((response) => response.responderId === user.id)
  );
  const confirmedResponses = responses.filter(
    (response) => response.status === "confirmed"
  );
  const rejectedResponses = responses.filter(
    (response) => response.status === "rejected"
  );

  const share = async () => {
    const text = shareMessage(match, isComplete);
    try {
      if (navigator.share) {
        await navigator.share({
          title: isComplete
            ? t("share.closedShareTitle")
            : t("share.shareTitle"),
          text,
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setActionError(t("share.shareError"));
    }
  };

  const join = () => {
    if (isAuthenticated && user && profile) {
      addResponse(
        { matchId: match.id, name: profile.name, userId: user.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["match", matchId] });
            queryClient.invalidateQueries({
              queryKey: ["responses", matchId],
            });
            queryClient.invalidateQueries({ queryKey: ["matches"] });
          },
          onError: () => {
            setActionError(t("status.joinError"));
          },
        }
      );
      return;
    }

    navigate(`/match/${match.id}`);
  };

  const close = () => {
    closeMatch(match.id, {
      onSuccess: () => navigate(APP_HOME_PATH),
      onError: () => {
        setActionError(t("status.closeError"));
      },
    });
  };

  return (
    <main className="pp-screen">
      <header className="pp-header">
        <Link to={APP_HOME_PATH} className="pp-back">
          {t("common.backHome")}
        </Link>
        <h1>{match.location}</h1>
      </header>

      <section className="pp-card">
        <p>📅 {match.date}</p>
        <p>⏰ {formatTime(match.time)}</p>
        {match.note && <p className="pp-muted">📝 {match.note}</p>}
      </section>

      <section className="pp-card" style={{ textAlign: "center" }}>
        {isComplete ? (
          <>
            <h2 style={{ color: "#10b981" }}>
              {match.status === "closed"
                ? t("status.closed")
                : t("status.complete")}
            </h2>
            <p className="pp-muted">
              {match.status === "closed"
                ? t("status.closedDescription")
                : t("status.completeDescription", { count: MAX_PLAYERS })}
            </p>
          </>
        ) : (
          <>
            <p className="pp-spots">
              {confirmed}/{MAX_PLAYERS}
            </p>
            <p className="pp-muted">{t("status.confirmed")}</p>
            <div className="pp-progress">
              <span style={{ width: `${progress}%` }} />
            </div>
          </>
        )}
      </section>

      {responses.length > 0 && (
        <section className="pp-card">
          {confirmedResponses.length > 0 && (
            <>
              <p className="pp-muted">{t("status.confirmed")}</p>
              {confirmedResponses.map((response) => (
                <p className="pp-person" key={response.id}>
                  <span>
                    {user && response.responderId === user.id ? "👤" : "✓"}
                  </span>
                  {response.name}
                </p>
              ))}
            </>
          )}
          {rejectedResponses.length > 0 && (
            <>
              <p className="pp-muted" style={{ marginTop: 12 }}>
                {t("status.cannot")}
              </p>
              {rejectedResponses.map((response) => (
                <p className="pp-person" key={response.id}>
                  <span>✕</span>
                  {response.name}
                </p>
              ))}
            </>
          )}
        </section>
      )}

      {!isComplete && (
        <section className="pp-card">
          <p>{t("status.waiting")}</p>
          <p className="pp-muted">
            {responses.length > 0
              ? t("status.responded", {
                  count: responses.length,
                  missing,
                })
              : t("status.sharePrompt")}
          </p>
        </section>
      )}

      {actionError && <p className="pp-error">{actionError}</p>}

      <div className="pp-actions">
        {isOwner && match.status !== "closed" && (
          <>
            {confirmClose ? (
              <div className="pp-confirm">
                <p>{t("status.closeConfirm")}</p>
                <button
                  type="button"
                  className="pp-btn-danger"
                  onClick={close}
                  disabled={isClosing}
                >
                  {isClosing ? t("status.closing") : t("status.close")}
                </button>
                <button
                  type="button"
                  className="pp-btn-secondary"
                  onClick={() => setConfirmClose(false)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="pp-btn-danger"
                onClick={() => setConfirmClose(true)}
              >
                {t("status.close")}
              </button>
            )}
          </>
        )}

        {!isComplete && !isOwner && !isAlreadyRegistered && (
          <button
            type="button"
            className="pp-btn"
            onClick={join}
            disabled={isJoining}
          >
            {isJoining ? t("status.joining") : t("status.join")}
          </button>
        )}

        <button type="button" className="pp-btn-secondary" onClick={share}>
          {copied ? t("share.copied") : t("common.share")}
        </button>
      </div>
    </main>
  );
};
